import React, { Component, useMemo } from "react";
import { useLocation, withRouter } from "react-router-dom";
import client from "@culturehq/client";
import { EmailField, Form, Info, Modal, PlainButton, StringField, SubmitButton, Success, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { removeQueryString, parseQueryString, t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModal from "./ChqModal";

class InviteModalContext extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
      success: false,
      successType: null,
      startOpen: props.startOpen
    };
  }

  handleSubmit = async ({ name, email }) => {
    this.setState({ success: false, error: false });

    try {
      const { invite, successType } = await client.sendInvite({ name, email });

      this.setState({ success: true, successType, user: invite.user, error: false });
    } catch (error) {
      this.setState({ success: false, error });
    }
  }

  handleReset = () => {
    this.setState({ success: false, error: false, successType: null, user: null });
  };

  render() {
    const { children, profile, onClose } = this.props;
    const { error, success, successType, user, startOpen } = this.state;

    const canInvite = profile.organizationAdmin || profile.organization.userCreatedInvitesEnabled;

    return (
      <ChqModal startOpen={startOpen} trigger={children} onClose={onClose} width="narrow">
        <Modal.Heading>
          <Trans>Invite a User to Join</Trans>
        </Modal.Heading>
        <Modal.Body>
          <p>
            <Trans>
              Invite users to join you in strengthening your organization&#39;s
              culture and community.
            </Trans>
          </p>

          {!canInvite && (
            <Warning>
              <Trans>Unable to invite users. Please ask your admin.</Trans>
            </Warning>
          )}

          <br />

          {canInvite && !success && (
            <>
              <Form onSubmit={this.handleSubmit}>
                {error && <ErrorMessage error={error} />}
                {success && <Success><Trans>Success!</Trans></Success>}

                <EmailField name="email" required>
                  Email
                </EmailField>
                <StringField name="name" required>
                  <Trans>Name</Trans>
                </StringField>
                <SubmitButton primary fillParent>
                  {submitting => (submitting ? t("Submitting...") : t("Send Invite"))}
                </SubmitButton>
              </Form>

              <p>
                <br />
                <i>
                  <Trans>
                    Note: As soon as your invites are sent, you can start
                    inviting these users to your events.
                  </Trans>
                </i>
              </p>
            </>
          )}

          {success && (
            <>
              {successType === "new_user" && (
                <Success>
                  <Trans>
                    Success! An invite has been sent to {user.email}. Why
                    don&#39;t you invite them to an event?
                  </Trans>
                </Success>
              )}

              {successType === "existing_user" && (
                <Info>
                  <Trans>
                    This user ({user.email}) has already been invited to the
                    platform. Remind them to check their email and/or invite
                    them to an event.
                  </Trans>
                </Info>
              )}

              <div className="text-center">
                <PlainButton onClick={this.handleReset} className="link">
                  <Trans>Invite Another User</Trans>
                </PlainButton>
              </div>
            </>
          )}
        </Modal.Body>
      </ChqModal>
    );
  }
}

const InviteModal = ({ children, history }) => {
  const { profile } = useProfile();
  const location = useLocation();

  const startOpen = useMemo(
    () => parseQueryString(location.search).action === "invite",
    [location.search]
  );

  const onClose = () => {
    const queryString = removeQueryString(location.search);
    history.push(`${location.pathname}?${queryString}`);
  };

  return (
    <InviteModalContext profile={profile} startOpen={startOpen} key={startOpen} onClose={onClose}>
      {children}
    </InviteModalContext>
  );
};

export default withRouter(InviteModal);
